<script
	lang="ts"
	module
>
	import type { Snippet } from 'svelte'
	import type { Promisable } from 'type-fest'

	export interface RowContext<Item> {
		item: Item
		isSelected: boolean
		isLoading: boolean
	}

	export interface DetailContext<Item, Result> {
		item: Item
		loadResult: Result | undefined
	}

	export interface Props<Item extends { key: string }, Result = unknown> {
		items: Array<Item>
		selectedKey?: string
		leftColumnClass?: string
		leftColumnHeightSubPx?: number
		rightColumnClass?: string
		rightColumnHeightSubPx?: number

		/** Searchable text for the filter input */
		getFilterText: (item: Item) => string
		/** Called before the selection changes. Return (or resolve) false to stay on the current item, e.g. when there are unsaved changes that couldn't be saved. */
		onBeforeSelect?: (item: Item) => Promisable<boolean>
		/** Called when the user selects a new item; the awaited result is passed to the `detail` snippet. */
		onSelect?: (item: Item) => Promisable<Result>
		/** Whether selecting the item that's already selected can keep its result instead of calling `onSelect` again, e.g. false for a failed load so it can be retried. Defaults to always. */
		canReuseResult?: (result: Result) => boolean
		/** Called before the narrow-screen detail modal closes, which unmounts the detail. Return (or resolve) false to keep it open, e.g. when there are unsaved changes that couldn't be saved. */
		onBeforeCloseDetail?: (item: Item) => Promisable<boolean>
		/** Whether the narrow-screen detail modal opens for the item that's selected when the list mounts. Defaults to true. */
		openDetailOnMount?: boolean

		listHeader?: Snippet
		/** Shown under the list, in the card footer */
		listFooter?: Snippet<[{ filteredItems: Array<Item> }]>
		row: Snippet<[RowContext<Item>]>
		detail: Snippet<[DetailContext<Item, Result>]>
		headerActions?: Snippet<[{ item: Item }]>
	}
</script>

<script
	lang="ts"
	generics="Item extends { key: string }, Result = unknown"
>
	// Ported from the InventoryListBase component in Pro Web, with the Pro-specific parts (template item creation, new part modal, session store, backend types) replaced by props and snippets.
	// Keep it free of app-specific imports, so it can be moved into a shared package later.
	import type { i18n } from 'types/common'

	import Input from '@isoftdata/svelte-input'
	import Modal from '@isoftdata/svelte-modal'
	import Button from '@isoftdata/svelte-button'
	import Card from '@isoftdata/svelte-card'
	import { MediaQuery } from 'svelte/reactivity'
	import { tick, getContext, onMount } from 'svelte'
	import { List, Item as ListItem } from '@isoftdata/svelte-list-group'

	let {
		items,
		selectedKey = $bindable(),
		leftColumnClass = 'col-lg-4 col-xl-3',
		leftColumnHeightSubPx = 390,
		rightColumnClass = 'col-lg-8 col-xl-9',
		rightColumnHeightSubPx = 281,
		getFilterText,
		onBeforeSelect,
		onSelect,
		canReuseResult = () => true,
		onBeforeCloseDetail,
		openDetailOnMount = true,
		listHeader,
		listFooter,
		row,
		detail,
		headerActions,
	}: Props<Item, Result> = $props()

	let loadingKey: string | undefined = $state(undefined)
	let filter = $state('')
	let loadResult: Result | undefined = $state(undefined)
	let detailModalOpen = $state(false)
	let filterInput = $state<HTMLInputElement>()

	const { t: translate } = getContext<i18n>('i18next')

	/** Counts calls to `selectItem`, so a load that finishes after a newer selection started is ignored */
	let latestSelectRequest = 0
	const isSmallScreen = new MediaQuery('(max-width: 991px)') // Bootstrap lg breakpoint

	function nextItem() {
		if (selectedIndex >= 0 && selectedIndex < filteredItems.length - 1) {
			selectItem(filteredItems[selectedIndex + 1])
		}
	}

	function previousItem() {
		if (selectedIndex > 0) {
			selectItem(filteredItems[selectedIndex - 1])
		}
	}

	/** `item` is either the Item object or a string representing the key of the item. Resolves to true if the item ended up selected. */
	export async function selectItem(item: Item | string, { openDetail = true } = {}): Promise<boolean> {
		await tick()
		if (typeof item === 'string') {
			const match = items.find(i => i.key === item)
			if (!match) {
				console.warn(`Could not find item with key ${item}`)
				return false
			}
			item = match
		}

		if (item.key === selectedKey && loadResult !== undefined && canReuseResult(loadResult)) {
			// On a narrow screen the detail is only shown in the modal, so selecting the item again reopens it
			if (openDetail && isSmallScreen.current) {
				detailModalOpen = true
			}
			return true
		}

		if (onBeforeSelect && !(await onBeforeSelect(item))) {
			return false
		}

		const request = ++latestSelectRequest
		if (onSelect) {
			loadingKey = item.key
			loadResult = undefined
			try {
				const result = await onSelect(item)
				// Another item was selected while this one loaded, so its result wins
				if (request !== latestSelectRequest) {
					return false
				}
				loadResult = result
			} finally {
				if (request === latestSelectRequest) {
					loadingKey = undefined
				}
			}
		}

		selectedKey = item.key

		if (openDetail && isSmallScreen.current) {
			detailModalOpen = true
		}

		await tick()
		document.querySelector('[data-selected="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		return true
	}

	/** Closing the modal unmounts the detail, so the parent gets a chance to save or keep it open first */
	async function closeDetailModal() {
		if (selectedItem && onBeforeCloseDetail && !(await onBeforeCloseDetail(selectedItem))) {
			return
		}
		detailModalOpen = false
	}

	const filteredItems = $derived(
		!filter
			? items
			: items.filter(item => {
					return getFilterText(item).toLowerCase().includes(filter.toLowerCase())
				}),
	)
	const selectedIndex = $derived(filteredItems.findIndex(item => item.key === selectedKey))
	const selectedItem = $derived(items.find(item => item.key === selectedKey))

	onMount(() => {
		const initialItem = items.find(item => item.key === selectedKey) ?? items[0]
		if (initialItem) {
			selectItem(initialItem.key, { openDetail: openDetailOnMount })
		}
	})
</script>

<div class="row">
	<div class={leftColumnClass}>
		<Card
			flushBody
			header={listHeader}
		>
			<Input
				showLabel={false}
				class="border-top-0 border-left-0 border-right-0 rounded-0"
				labelParentClass="mb-1"
				label={translate('common:filter', 'Filter')}
				placeholder={`${translate('common:filter', 'Filter')}...`}
				lazy={200}
				bind:input={filterInput}
				bind:value={filter}
			>
				{#snippet append()}
					<Button
						outline
						color="danger"
						icon="xmark-large"
						class="rounded-0"
						disabled={!filter}
						onclick={async () => {
							filter = ''
							await tick()
							filterInput?.focus()
						}}
					/>
				{/snippet}
			</Input>
			<div style="overflow-y: auto; height: calc(100vh - {leftColumnHeightSubPx}px); overflow-x: hidden;">
				<List
					flush
					items={filteredItems}
				>
					{#each filteredItems as item (item.key)}
						<ListItem
							data-selected={selectedKey === item.key ? 'true' : undefined}
							color={item.key === selectedKey && !isSmallScreen.current ? 'primary' : undefined}
							onclick={() => selectItem(item)}
						>
							{@render row({
								item,
								isSelected: item.key === selectedKey,
								isLoading: loadingKey === item.key,
							})}
						</ListItem>
					{/each}
				</List>
			</div>
			{#snippet footer()}
				{@render listFooter?.({ filteredItems })}
			{/snippet}
		</Card>
	</div>
	{#if !isSmallScreen.current}
		<div class={rightColumnClass}>
			<Card bodyStyle="overflow-y: auto; height: calc(100vh - {rightColumnHeightSubPx}px)">
				{#snippet header()}
					<h4 class="mb-0">
						{#if selectedItem}
							<div class="d-flex align-items-center justify-content-between">
								<div
									class="flex-grow-1"
									style="min-width: 0;"
								>
									{@render row({
										item: selectedItem,
										isSelected: true,
										isLoading: loadingKey === selectedItem.key,
									})}
								</div>
								<div class="flex-shrink-0 ms-2">
									{@render headerControls()}
								</div>
							</div>
						{:else}
							<span class="fst-italic mb-0"
								>{translate('inventoryList.selectItemToViewDetails', 'Select an item to view details')}</span
							>
						{/if}
					</h4>
				{/snippet}
				{@render detailPanel()}
			</Card>
		</div>
	{/if}
</div>

<Modal
	cancelShown={false}
	closeShown={false}
	title=""
	size="xl"
	confirmButtonText={translate('common:done', 'Done')}
	bind:show={detailModalOpen}
	confirm={closeDetailModal}
	close={closeDetailModal}
>
	{#snippet header()}
		<h5 class="mb-0 w-100">
			<div class="d-flex align-items-center justify-content-between">
				<div
					class="flex-grow-1"
					style="min-width: 0;"
				>
					{#if selectedItem}
						{@render row({
							item: selectedItem,
							isSelected: true,
							isLoading: loadingKey === selectedItem.key,
						})}
					{/if}
				</div>
				<div class="flex-shrink-0 ms-2">{@render headerControls()}</div>
			</div>
		</h5>
	{/snippet}
	<div style="overflow-y: auto; max-height: calc(100vh - 320px); overflow-x: hidden;">
		{@render detailPanel()}
	</div>
</Modal>

{#snippet detailPanel()}
	{#if !loadingKey && selectedItem}
		{@render detail({
			item: selectedItem,
			loadResult,
		})}
	{:else if loadingKey}
		<div class="text-center text-body-secondary py-5">
			<i class="fas fa-spinner fa-spin fa-2x"></i>
		</div>
	{/if}
{/snippet}

{#snippet headerControls()}
	{#if selectedItem}
		{@render headerActions?.({ item: selectedItem })}
	{/if}
	<Button
		outline
		icon="arrow-up"
		disabled={!!loadingKey || selectedIndex <= 0}
		onclick={previousItem}
	/>
	<Button
		outline
		icon="arrow-down"
		disabled={!!loadingKey || selectedIndex < 0 || selectedIndex === filteredItems.length - 1}
		onclick={nextItem}
	/>
{/snippet}
